import { useI18n } from 'vue-i18n';
import { $api } from './useApi';
import type { PackageSectionData } from '@/models/package';
import type { SlugLocale, UseCaseResponse } from '@/models/service';
import type { InfoSectionData } from '@/models/info';
import type { HeroSectionData } from '@/models/hero';

export const useUseCaseContent = async () => {
  const route = useRoute();

  const slug = route.params.slug as string;

  const { locale } = useI18n();

  const { data } = await useAsyncData(`use-case-data-${slug}-${locale}`, async () => {
    return await $api<UseCaseResponse>(`/use-case/${slug}`);
  });

  const highlightsSectionData = computed(() => data.value?.highlights ?? []);

  const highlightSectionTitle = computed<string>(() => data.value?.highlightSectionTitle ?? '');

  const slugs = computed<SlugLocale[]>(() => data.value?.slugs ?? []);

  const packageSectionData = computed<PackageSectionData | undefined>(() => {
    return !data.value
      ? undefined
      : {
          packageSectionSubtitle: data.value.packageSectionSubtitle,
          packageSectionTitle: data.value.packageSectionTitle,
          packages: data.value.packages
        };
  });

  const infoSectionData = computed<InfoSectionData | undefined>(() => {
    return !data.value
      ? undefined
      : {
          infoSectionTitle: data.value.infoSectionTitle,
          infoSectionContent: data.value.infoSectionContent,
          infoSectionImage: data.value.infoSectionImage,
          showInfoSection: data.value.showInfoSection
        };
  });

  const heroSectionData = computed<HeroSectionData | undefined>(() => {
    return !data.value
      ? undefined
      : {
          title: data.value.title,
          heroTitle: data.value.heroTitle,
          heroSubtitle: data.value.heroSubtitle,
          icon: data.value.icon,
          cover: data.value.cover,
          heroMessages: data.value.heroMessages.map(
            (item: any) =>
              ({
                title: item.title,
                subtitle: item.subtitle
              }) as any
          )
        };
  });

  const i18nParams = computed(() => ({
    en: { slug: findLocaleSlug('en') },
    hu: { slug: findLocaleSlug('hu') }
  }));

  const findLocaleSlug = (locale: string) => {
    return slugs.value?.find((item) => item.locale === locale)?.slug || 'not-found';
  };

  return {
    slug,
    highlightsSectionData,
    highlightSectionTitle,
    packageSectionData,
    infoSectionData,
    heroSectionData,
    i18nParams
  };
};
